import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { waitFor } from '@ember/test-waiters';
import { service } from '@ember/service';
import type { Registry as Services } from '@ember/service';
import BaseController from 'uplisting-frontend/pods/base/controller';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import {
  AiSettingTone,
  AiSettingGuestAddressing,
  AiSettingIdentity,
} from 'uplisting-frontend/models/ai-setting';

export default class AiMessagingControlsController extends BaseController {
  queryParams = ['tab', 'property'];

  @service sentry!: Services['sentry'];
  @service featureFlags!: Services['feature-flags'];
  @service('http-client') httpClient!: Services['http-client'];

  @tracked settings: ModelRegistry['ai-setting'][] = [];
  @tracked properties: ModelRegistry['property'][] = [];
  @tracked tab: string | null = null;
  @tracked property: string | null = null;
  @tracked isSaving = false;
  @tracked currentTone: AiSettingTone = AiSettingTone.friendly;
  @tracked currentGuestAddressing: AiSettingGuestAddressing =
    AiSettingGuestAddressing.firstName;
  @tracked currentIdentity: AiSettingIdentity = AiSettingIdentity.asHost;
  @tracked currentAutoReplyEnabled = false;
  @tracked currentAutoReplyDelaySeconds = 900;
  @tracked showAutoReplyConfirm = false;

  get activeTab(): 'global' | 'property' {
    return this.tab === 'property' ? 'property' : 'global';
  }

  get selectedProperty(): ModelRegistry['property'] | undefined {
    if (!this.property) return undefined;
    return this.properties.find((p) => p.id === this.property);
  }

  get canSeeAutoReply(): boolean {
    return this.featureFlags.gate.enabled('ai_auto_reply');
  }

  get accountSetting(): ModelRegistry['ai-setting'] | undefined {
    return this.settings.find((s) => !s.isPropertyOverride);
  }

  get propertyOverrides(): ModelRegistry['ai-setting'][] {
    return this.settings.filter(
      (s) => s.isPropertyOverride && this.overrideSummary(s) !== '',
    );
  }

  get activeSetting(): ModelRegistry['ai-setting'] | undefined {
    if (this.activeTab === 'global') {
      return this.accountSetting;
    }

    if (this.selectedProperty) {
      return this.settings.find(
        (s) => s.belongsTo('property').id() === this.selectedProperty?.id,
      );
    }

    return undefined;
  }

  get showControls(): boolean {
    return this.activeTab === 'global' || !!this.selectedProperty;
  }

  get toneOptions(): { value: string; labelKey: string; descKey: string }[] {
    return ['friendly', 'professional', 'casual', 'concise'].map((v) => ({
      value: v,
      labelKey: `ai_controls.tone.${v}.label`,
      descKey: `ai_controls.tone.${v}.description`,
    }));
  }

  get identityOptions(): {
    value: string;
    labelKey: string;
    descKey: string;
  }[] {
    return ['as_host', 'as_assistant'].map((v) => ({
      value: v,
      labelKey: `ai_controls.identity.${v}.label`,
      descKey: `ai_controls.identity.${v}.description`,
    }));
  }

  get guestAddressingOptions(): {
    value: string;
    labelKey: string;
    descKey: string;
  }[] {
    return ['first_name', 'full_name', 'no_name'].map((v) => ({
      value: v,
      labelKey: `ai_controls.guest_addressing.${v}.label`,
      descKey: `ai_controls.guest_addressing.${v}.description`,
    }));
  }

  get delayMinutes(): number {
    return Math.round(this.currentAutoReplyDelaySeconds / 60);
  }

  get delayLabel(): string {
    if (this.currentAutoReplyDelaySeconds === 0) {
      return this.intl.t('ai_controls.delay.instant') as string;
    }
    return this.intl.t('ai_controls.delay.value', {
      mins: this.delayMinutes,
    }) as string;
  }

  get delayRangeStyle(): string {
    const pct = (this.currentAutoReplyDelaySeconds / 3600) * 100;
    return `background: linear-gradient(to right, var(--primary-green) ${pct}%, var(--grey-scale-200) ${pct}%)`;
  }

  get confidenceRangeStyle(): string {
    const pct = ((85 - 50) / (100 - 50)) * 100;
    return `background: linear-gradient(to right, var(--primary-green) ${pct}%, var(--grey-scale-200) ${pct}%)`;
  }

  @action
  overrideSummary(setting: ModelRegistry['ai-setting']): string {
    const defaults = this.accountSetting;
    const defaultTone = defaults?.tone ?? AiSettingTone.friendly;
    const defaultAddressing =
      defaults?.guestAddressing ?? AiSettingGuestAddressing.firstName;
    const defaultIdentity = defaults?.identity ?? AiSettingIdentity.asHost;
    const diffs: string[] = [];

    const defaultAutoReply = defaults?.autoReplyEnabled ?? false;
    const autoReplyValue = setting.autoReplyEnabled;
    if (
      autoReplyValue !== undefined &&
      autoReplyValue !== null &&
      autoReplyValue !== defaultAutoReply
    ) {
      diffs.push(
        autoReplyValue
          ? (this.intl.t('ai_controls.automation.override_active') as string)
          : (this.intl.t('ai_controls.automation.override_inactive') as string),
      );
    }

    const defaultDelay = defaults?.autoReplyDelaySeconds ?? 900;
    const delayValue = setting.autoReplyDelaySeconds;
    if (
      delayValue !== undefined &&
      delayValue !== null &&
      delayValue !== defaultDelay
    ) {
      const mins = Math.round(delayValue / 60);
      diffs.push(
        mins === 0
          ? (this.intl.t('ai_controls.delay.instant') as string)
          : (this.intl.t('ai_controls.delay.override_summary', {
              mins,
            }) as string),
      );
    }

    if (setting.tone && setting.tone !== defaultTone) {
      diffs.push(
        this.intl.t(`ai_controls.tone.${setting.tone}.label`) as string,
      );
    }

    if (setting.identity && setting.identity !== defaultIdentity) {
      diffs.push(
        this.intl.t(`ai_controls.identity.${setting.identity}.label`) as string,
      );
    }

    if (
      setting.guestAddressing &&
      setting.guestAddressing !== defaultAddressing
    ) {
      diffs.push(
        this.intl.t(
          `ai_controls.guest_addressing.${setting.guestAddressing}.label`,
        ) as string,
      );
    }

    return diffs.join(' · ');
  }

  get selectedPropertyName(): string | undefined {
    return this.selectedProperty?.nickname;
  }

  @action
  handleTabChange(tab: 'global' | 'property'): void {
    this.tab = tab === 'global' ? null : 'property';
    this.property = null;
    this.syncCurrentValues();
  }

  @action
  handlePropertyChange(property: ModelRegistry['property']): void {
    this.property = property?.id ?? null;
    this.syncCurrentValues();
  }

  @action
  selectOverrideProperty(setting: ModelRegistry['ai-setting']): void {
    this.property = setting.property?.id ?? null;
    this.syncCurrentValues();
  }

  @action
  @waitFor
  async updateTone(tone: string): Promise<void> {
    this.currentTone = tone as AiSettingTone;
    await this.upsertSetting({ tone: tone as AiSettingTone });
  }

  @action
  @waitFor
  async updateGuestAddressing(guestAddressing: string): Promise<void> {
    this.currentGuestAddressing = guestAddressing as AiSettingGuestAddressing;
    await this.upsertSetting({
      guestAddressing: guestAddressing as AiSettingGuestAddressing,
    });
  }

  @action
  @waitFor
  async updateIdentity(identity: string): Promise<void> {
    this.currentIdentity = identity as AiSettingIdentity;
    await this.upsertSetting({ identity: identity as AiSettingIdentity });
  }

  get autoReplyConfirmTitle(): string {
    const action = this.currentAutoReplyEnabled ? 'disable' : 'enable';
    const scope = this.activeTab === 'property' ? 'property' : 'global';
    return this.intl.t(
      `ai_controls.automation.confirm_${action}.${scope}.title`,
    ) as string;
  }

  get autoReplyConfirmBody(): string {
    const action = this.currentAutoReplyEnabled ? 'disable' : 'enable';
    const scope = this.activeTab === 'property' ? 'property' : 'global';
    return this.intl.t(
      `ai_controls.automation.confirm_${action}.${scope}.body`,
    ) as string;
  }

  @action
  promptAutoReplyToggle(): void {
    this.showAutoReplyConfirm = true;
  }

  @action
  cancelAutoReplyToggle(): void {
    this.showAutoReplyConfirm = false;
  }

  @action
  @waitFor
  async confirmAutoReplyToggle(): Promise<void> {
    this.showAutoReplyConfirm = false;
    this.currentAutoReplyEnabled = !this.currentAutoReplyEnabled;
    await this.upsertSetting({
      autoReplyEnabled: this.currentAutoReplyEnabled,
    });
  }

  @action
  handleDelayInput(event: Event): void {
    this.currentAutoReplyDelaySeconds = parseInt(
      (event.target as HTMLInputElement).value,
      10,
    );
  }

  @action
  @waitFor
  async updateAutoReplyDelay(event: Event): Promise<void> {
    const value = parseInt((event.target as HTMLInputElement).value, 10);
    this.currentAutoReplyDelaySeconds = value;
    await this.upsertSetting({ autoReplyDelaySeconds: value });
  }

  syncCurrentValues(): void {
    const setting = this.activeSetting;
    const fallback = this.accountSetting;
    this.currentTone =
      setting?.tone ?? fallback?.tone ?? AiSettingTone.friendly;
    this.currentGuestAddressing =
      setting?.guestAddressing ??
      fallback?.guestAddressing ??
      AiSettingGuestAddressing.firstName;
    this.currentIdentity =
      setting?.identity ?? fallback?.identity ?? AiSettingIdentity.asHost;
    this.currentAutoReplyEnabled =
      (setting?.autoReplyEnabled !== null &&
      setting?.autoReplyEnabled !== undefined
        ? setting.autoReplyEnabled
        : fallback?.autoReplyEnabled) ?? false;
    this.currentAutoReplyDelaySeconds =
      (setting?.autoReplyDelaySeconds !== null &&
      setting?.autoReplyDelaySeconds !== undefined
        ? setting.autoReplyDelaySeconds
        : fallback?.autoReplyDelaySeconds) ?? 900;
  }

  private async upsertSetting(
    attrs: Partial<{
      tone: AiSettingTone;
      guestAddressing: AiSettingGuestAddressing;
      identity: AiSettingIdentity;
      autoReplyEnabled: boolean;
      autoReplyDelaySeconds: number;
    }>,
  ): Promise<void> {
    this.isSaving = true;

    try {
      const propertyId =
        this.activeTab === 'property' ? this.selectedProperty?.id : null;

      const existing = this.activeSetting;

      await this.httpClient.postJSONApi('v2/ai_settings', {
        type: 'ai-settings',
        attributes: {
          tone: attrs.tone ?? this.currentTone,
          guest_addressing:
            attrs.guestAddressing ?? this.currentGuestAddressing,
          identity: attrs.identity ?? this.currentIdentity,
          auto_reply_enabled:
            attrs.autoReplyEnabled ?? this.currentAutoReplyEnabled,
          auto_reply_delay_seconds:
            attrs.autoReplyDelaySeconds ?? this.currentAutoReplyDelaySeconds,
        },
        relationships: {
          property: {
            data: propertyId ? { type: 'properties', id: propertyId } : null,
          },
        },
      });

      if (existing) {
        existing.tone = this.currentTone;
        existing.guestAddressing = this.currentGuestAddressing;
        existing.identity = this.currentIdentity;
        existing.autoReplyEnabled = this.currentAutoReplyEnabled;
        existing.autoReplyDelaySeconds = this.currentAutoReplyDelaySeconds;
      }

      if (!existing) {
        await this.store.findAll('ai-setting', { reload: true });
        await this.store.findAll('property', { reload: true });
        this.properties = this.store.peekAll('property').slice();
      }

      this.refreshSettings();

      this.notifications.info('ai_controls.notifications.saved');
    } catch (error) {
      this.sentry.captureException(error);
      this.notifications.error();
      this.syncCurrentValues();
    } finally {
      this.isSaving = false;
    }
  }

  private refreshSettings(): void {
    this.settings = this.store
      .peekAll('ai-setting')
      .filter((s) => !s.isNew && !s.isDeleted)
      .slice();
    this.syncCurrentValues();
  }
}
